// Pricing tier feature control (see FeatureControl screen / api/FeatureControl).
// A company without a tier is unrestricted: every feature is on, exactly as before.

export const SUPER_ADMIN_EMAIL = "super@admin.com";

export const FEATURE_KEYS = {
  DONOR_STATUS: "DONOR_STATUS",
  DONOR_INTERACTIONS: "DONOR_INTERACTIONS",
  DONOR_ASSIGNMENT: "DONOR_ASSIGNMENT",
  CAMPAIGN_ROI: "CAMPAIGN_ROI",
  GRANTS: "GRANTS",
  FUNDRAISING: "FUNDRAISING",
  PLEDGES: "PLEDGES",
  IN_KIND_DONATIONS: "IN_KIND_DONATIONS",
  CUSTOM_ROLES: "CUSTOM_ROLES",
  GRANULAR_PERMISSIONS: "GRANULAR_PERMISSIONS",
  EXPORT_RESTRICTION: "EXPORT_RESTRICTION",
  INTERACTION_ASSIGNMENT: "INTERACTION_ASSIGNMENT",
};

export const UNRESTRICTED_FEATURES = { isRestricted: false, features: [] };

// Menu entry SuperAdmin always gets; other users get it only through a "FeatureControl"
// role created from the Roles screen and assigned in Permission.
export const FEATURE_CONTROL_ROLE = {
  roleId: -1001,
  parentId: 0,
  isLeaveNode: true,
  isMenuPart: true,
  roleName: "FeatureControl",
  menuName: "FeatureControl",
  path: "FeatureControl",
  title: "Feature Control",
  icon: "FaSliders",
  companyId: null,
  isActive: true,
  isDelete: false,
};

// Menu entry SuperAdmin always gets; other users get it only through a "WhatsAppDemo"
// role created from the Roles screen and assigned in Permission.
export const WHATSAPP_DEMO_ROLE = {
  roleId: -1002,
  parentId: 0,
  isLeaveNode: true,
  isMenuPart: true,
  roleName: "WhatsAppDemo",
  menuName: "WhatsAppDemo",
  path: "WhatsAppDemo",
  title: "WhatsApp Demo",
  icon: "FaWhatsapp",
  companyId: null,
  isActive: true,
  isDelete: false,
};

const SUPER_ADMIN_ONLY_ROLES = [FEATURE_CONTROL_ROLE, WHATSAPP_DEMO_ROLE];

const CACHE_PREFIX = "companyFeatures_";

export const readCachedCompanyFeatures = (companyId) => {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + companyId);
    const parsed = cached ? JSON.parse(cached) : null;
    return parsed && parsed.isRestricted ? parsed : UNRESTRICTED_FEATURES;
  } catch (e) {
    return UNRESTRICTED_FEATURES;
  }
};

export const cacheCompanyFeatures = (companyId, features) => {
  try {
    if (features && features.isRestricted) {
      localStorage.setItem(CACHE_PREFIX + companyId, JSON.stringify(features));
    } else {
      localStorage.removeItem(CACHE_PREFIX + companyId);
    }
  } catch (e) {
    // storage unavailable; the live fetch still applies
  }
};

export const isSuperAdminUser = (users) =>
  (users || []).some(
    (user) => (user?.email || "").toLowerCase() === SUPER_ADMIN_EMAIL
  );

export const isFeatureEnabledFor = (companyFeatures, featureKey) => {
  if (!companyFeatures || !companyFeatures.isRestricted) return true;
  const key = (featureKey || "").toUpperCase();
  const feature = (companyFeatures.features || []).find(
    (f) => (f.featureKey || "").toUpperCase() === key
  );
  // Keys that are not in the catalogue are never gated.
  return feature ? feature.isEnabled : true;
};

export const getDisabledMenuPaths = (companyFeatures) => {
  const paths = new Set();
  if (!companyFeatures || !companyFeatures.isRestricted) return paths;
  (companyFeatures.features || [])
    .filter((f) => !f.isEnabled && f.menuPaths)
    .forEach((f) =>
      f.menuPaths
        .split(",")
        .map((p) => p.trim().toLowerCase())
        .filter(Boolean)
        .forEach((p) => paths.add(p))
    );
  return paths;
};

// Removes roles whose path belongs to a disabled feature, their children, and menu
// groups left empty. Returns the same array untouched when nothing applies.
export const filterRolesByFeatures = (roles, companyFeatures) => {
  const disabledPaths = getDisabledMenuPaths(companyFeatures);
  if (!roles || disabledPaths.size === 0) return roles;

  const id = (value) => String(value);
  const hidden = new Set(
    roles
      .filter((r) => r.path && disabledPaths.has(String(r.path).toLowerCase()))
      .map((r) => id(r.roleId))
  );
  if (hidden.size === 0) return roles;

  const isHidden = (r) => hidden.has(id(r.roleId));
  const childrenOf = (r) => roles.filter((c) => id(c.parentId) === id(r.roleId));
  let changed = true;
  while (changed) {
    changed = false;
    for (const r of roles) {
      if (isHidden(r)) continue;
      const children = childrenOf(r);
      const emptyGroup =
        r.isLeaveNode === false &&
        children.length > 0 &&
        children.every(isHidden);
      if (hidden.has(id(r.parentId)) || emptyGroup) {
        hidden.add(id(r.roleId));
        changed = true;
      }
    }
  }
  return roles.filter((r) => !hidden.has(id(r.roleId)));
};

export const applyFeatureControlToRoles = (
  roles,
  companyFeatures,
  isSuperAdmin
) => {
  if (!roles) return roles;
  if (isSuperAdmin) {
    // Add only the screens not already present as real roles for this company.
    const hasPath = (path) =>
      roles.some((r) => String(r.path || "").toLowerCase() === path.toLowerCase());
    const missing = SUPER_ADMIN_ONLY_ROLES.filter((r) => !hasPath(r.path));
    return missing.length ? [...roles, ...missing] : roles;
  }
  return filterRolesByFeatures(roles, companyFeatures);
};
